/**
 * Expanded coding philosophy with MUCH deeper dives
 * Based on the full coding-philosophy.md document
 */
import { DetailedPrinciple } from '../types';

export const detailedCodingPhilosophy: DetailedPrinciple[] = [
  {
    id: 'defensive-engineering',
    title: 'The Defensive Engineering Mindset',
    subtitle: 'Everything Eventually Breaks',
    overview: 'I assume failure at every level. Not pessimism - realism. Networks drop. Disks fill. Users click buttons in unexpected orders. Files get corrupted. Memory runs out. APIs change. External services go down. When I write code, I\'m not asking "will this work?" I\'m asking "when this fails, how quickly can someone - including future me - diagnose and fix it?"',
    sections: [
      {
        heading: 'The Hierarchy of Concerns',
        content: [
          'When writing any piece of functionality, I think in this order:',
          '',
          '**First: Can it fail safely?** If something goes wrong, does the system crash? Does it corrupt data? Does it leave the user confused? Or does it fail gracefully with clear guidance?',
          '',
          '**Second: Will someone understand what happened?** When this code breaks at 2am, will the error message tell whoever is debugging exactly what went wrong, where, and why?',
          '',
          '**Third: Is it correct?** Only after ensuring safe failure and clear errors do I focus on making it work correctly.',
          '',
          '**Fourth: Is it fast?** Performance matters, but correctness and debuggability matter more.',
          '',
          'This ordering is deliberate. A slow but reliable system is better than a fast but fragile one. A system that fails with clear error messages is better than one that fails silently.'
        ]
      },
      {
        heading: 'Validate Everything, Trust Nothing',
        content: [
          'Before performing any operation, I validate that it can succeed. But I don\'t just check one thing - I check at multiple layers because different checks catch different classes of failures.',
          '',
          'When reading a file, I don\'t just check if it exists. I check:'
        ],
        examples: [
          'Does the path exist? (user configuration error)',
          'Is it actually a file, not a directory? (logic bug)',
          'Do we have permission to read it? (deployment issue)',
          'Can we actually open it? (concurrent access)',
          'Does the read operation succeed? (hardware problem)'
        ]
      },
      {
        heading: 'Check Before You Check',
        content: [
          'Before validating something, validate that you CAN validate it.',
          '',
          'Before checking if an API endpoint exists, check if you have internet connectivity. Before checking internet connectivity, check if the network interface is up.',
          '',
          'Each layer of validation should verify its own prerequisites. This creates a cascade of increasingly specific error messages that pinpoint exactly where the failure occurred.'
        ]
      }
    ]
  },
  {
    id: 'single-source-truth',
    title: 'The Single Source of Truth Architecture',
    subtitle: 'Solving the State Synchronization Problem',
    overview: 'Complex applications have a fundamental challenge: how do you keep multiple interfaces synchronized? If you have a desktop GUI, a web interface, and a CLI all accessing the same data, how do you ensure they all see the same state? How do you prevent them from diverging?',
    sections: [
      {
        heading: 'The Wrong Approach: Duplicated Logic',
        content: [
          'The naive approach is to implement business logic in each interface. The GUI has its own logic. The web interface has its own logic. The CLI has its own logic.',
          '',
          'This is a maintenance disaster. Bugs fixed in one interface remain in others. Features added to one interface are missing in others. Each interface has slightly different behavior. State diverges because each maintains its own copy.'
        ]
      },
      {
        heading: 'The Right Approach: Central Orchestrator',
        content: [
          'Create one class that owns all application state. This orchestrator contains all business logic. It manages the database. It coordinates operations. It enforces rules.',
          '',
          'Every interface - GUI, web, CLI - uses the exact same orchestrator. They don\'t implement business logic. They don\'t maintain state. They ask the orchestrator to do work and listen for updates.',
          '',
          'Communication happens through publish-subscribe messaging. The orchestrator broadcasts "status update" or "error occurred" or "data changed." All interfaces subscribe to these broadcasts and update accordingly.',
          '',
          'The orchestrator doesn\'t know who\'s listening. Interfaces don\'t know who\'s publishing. They\'re decoupled.'
        ]
      },
      {
        heading: 'Why This Works',
        content: [
          'When you fix a bug in the orchestrator, all interfaces benefit immediately.',
          'When you add a feature to the orchestrator, all interfaces can use it.',
          'When you change business logic, you change it in exactly one place.',
          '',
          'Want to add a new interface? Just subscribe to the existing broadcasts. No changes to existing code required.',
          '',
          'Testing becomes simple: mock the message broker, test the orchestrator in isolation.'
        ]
      }
    ]
  },
  {
    id: 'dry-obsession',
    title: 'The DRY Obsession',
    subtitle: 'Hunt Down Duplication Aggressively',
    overview: 'I don\'t just avoid duplicating code. I actively hunt it down and eliminate it. When I write similar logic twice, I stop. I don\'t write it a third time. I pause, extract the pattern into a utility, and use that utility everywhere.',
    sections: [
      {
        heading: 'Pattern Recognition',
        content: [
          'The key is recognizing when duplication is happening, even when the code looks different.',
          '',
          'If I see database connection logic scattered across files - some with retry logic, some without, some handling errors one way, others handling them differently - that\'s duplication even though the actual code varies.',
          '',
          'If I see show name normalization happening in multiple places - lowercasing here, removing special characters there, applying mappings elsewhere - that\'s duplication even though each implementation differs.',
          '',
          'The pattern is: "We\'re solving the same problem in multiple places." That means extract it.'
        ]
      },
      {
        heading: 'When to Extract',
        content: [
          '**Extract when:**'
        ],
        examples: [
          'The same problem appears twice',
          'The pattern will likely recur',
          'Behavior needs to be consistent across the codebase',
          'The logic is complex enough that bugs could hide in it'
        ]
      },
      {
        heading: 'The Utility Philosophy',
        content: [
          'Once you extract something into a utility, you\'ve created a contract. Every module that uses it now depends on consistent behavior.',
          '',
          'This is powerful: change the utility once, every user benefits. Add a feature to the utility, everyone gets it. Fix a bug in the utility, it\'s fixed everywhere.',
          '',
          'But it\'s also a responsibility: breaking changes affect everyone. The utility must be well-designed because you can\'t easily change it later.'
        ]
      }
    ]
  },
  {
    id: 'cross-cutting-concerns',
    title: 'Centralized Cross-Cutting Concerns',
    subtitle: 'Error Handling, Logging, Database Access',
    overview: 'Some functionality appears everywhere in a codebase: error handling, logging, database access, configuration, authentication. If these are implemented ad-hoc throughout the code, you get inconsistency, bugs, and maintenance nightmares. Instead, centralize them.',
    sections: [
      {
        heading: 'Error Management',
        content: [
          'Every module needs to report errors. If each module implements its own error handling, you get inconsistent error formats, some errors logged and others ignored, no way to aggregate errors, and different user experiences depending on which module failed.',
          '',
          'Create a centralized error management system. Every module reports errors to it. The error manager:'
        ],
        examples: [
          'Formats errors consistently',
          'Broadcasts them to all interfaces',
          'Maintains error history for debugging',
          'Applies consistent severity levels'
        ]
      },
      {
        heading: 'Database Management',
        content: [
          'Every module that touches the database should use the same access layer. This layer provides thread-safe connections, automatic retry logic for locks, transaction support, and consistent error handling.',
          '',
          'Without this, you get database locks because some code retries and some doesn\'t. You get race conditions because some code uses transactions and some doesn\'t. You get inconsistent error messages because each module handles database errors differently.'
        ]
      },
      {
        heading: 'The Singleton Pattern for Global Concerns',
        content: [
          'For truly global concerns - things that should have exactly one instance in the entire application - use the singleton pattern.',
          '',
          'Database managers don\'t need multiple instances. Error managers don\'t need multiple instances. Message brokers don\'t need multiple instances.',
          '',
          'One instance, globally accessible, with thread-safe initialization.'
        ]
      }
    ]
  },
  {
    id: 'pub-sub-communication',
    title: 'Communication Through Pub-Sub',
    subtitle: 'Loose Coupling and Real-Time Synchronization',
    overview: 'Components should not call each other directly. Direct calls create tight coupling. If Component A calls Component B, then A depends on B existing. A needs to know B\'s interface. Testing A requires mocking B. Changes to B can break A.',
    sections: [
      {
        heading: 'The Pub-Sub Alternative',
        content: [
          'Components publish events. Other components subscribe to events.',
          '',
          'When processing completes, publish "processing complete." When an error occurs, publish "error occurred." When state changes, publish "state changed."',
          '',
          'Any component that cares can subscribe. The publisher doesn\'t know who\'s listening. The subscribers don\'t know who\'s publishing.'
        ]
      },
      {
        heading: 'Benefits',
        content: [
          'Add a new interface without changing existing code - just subscribe to existing events.',
          'Test components in isolation - mock the message broker.',
          'Change implementations without breaking dependents - the event contract remains stable.',
          'Scale by adding listeners - publishers don\'t care how many subscribers exist.'
        ]
      },
      {
        heading: 'Real-Time Synchronization',
        content: [
          'When the orchestrator broadcasts state changes through pub-sub, all interfaces update automatically. The GUI updates. The web interface updates. The CLI updates. They all see the same state at the same time.',
          '',
          'No explicit synchronization code. No polling. No stale data. Just automatic propagation of state changes.'
        ]
      }
    ]
  },
  {
    id: 'threading-concurrency',
    title: 'Threading and Concurrency',
    subtitle: 'Design for Threads from Day One',
    overview: 'I don\'t add threading as an afterthought. I design for it from the beginning. Why? Because retrofitting concurrency into a single-threaded codebase is exponentially harder than building it in from the start. Every long-running operation runs in a background thread. The user interface never blocks. Progress updates broadcast in real-time. Operations can be cancelled mid-flight.',
    sections: [
      {
        heading: 'Thread-Safe by Default',
        content: [
          'All shared utilities must be thread-safe. Database managers use thread-local connections. Message brokers use locks. Singletons initialize atomically.',
          '',
          'This isn\'t optional. If a utility isn\'t thread-safe, you will hit race conditions. They might not appear in development, but they will appear in production.'
        ]
      },
      {
        heading: 'The Main Thread Rule',
        content: [
          'Never, ever block the main thread.',
          '',
          'If the user clicks a button that triggers a 10-minute operation, that operation runs in a background thread. The UI remains responsive. The user sees progress. They can cancel if needed.',
          '',
          'Blocking the main thread is how you get "Application Not Responding" dialogs and users who think your software crashed.'
        ]
      }
    ]
  },
  {
    id: 'status-broadcasting',
    title: 'Status Broadcasting',
    subtitle: 'Users Deserve to Know What\'s Happening',
    overview: 'When your application is doing something, the user should know: What it\'s doing, how far along it is, and whether it\'s working or stuck. Never show fake progress bars. Never estimate completion times you can\'t actually calculate. Never say "Done" when there were errors. Report actual progress based on actual work completed.',
    sections: [
      {
        heading: 'The Status Bar Never Lies',
        content: [
          'Report actual progress based on actual work completed. Not estimated. Not guessed. Actual.',
          '',
          'If you can\'t calculate real progress (like when downloading from a server that doesn\'t provide Content-Length), show an indeterminate spinner and status text. Don\'t fake it.'
        ]
      },
      {
        heading: 'Granular Updates',
        content: [
          'For long operations, broadcast status at every significant step.',
          '',
          'Not just "Processing..." for 10 minutes.',
          '',
          '"Processing file 1 of 100..."',
          '"Processing file 2 of 100..."',
          '"Processing file 3 of 100..."',
          '',
          'Users can see it\'s working. They can estimate completion. If it crashes, the last status update shows exactly where.'
        ]
      },
      {
        heading: 'Error Context in Status',
        content: [
          'When an error occurs, the status should explain what we were trying to do, what went wrong, and what the user should do next.',
          '',
          'Not just "Error." Not just "Failed." Clear, actionable information.'
        ]
      }
    ]
  },
  {
    id: 'error-handling',
    title: 'Error Handling Philosophy',
    subtitle: 'The Five Components of a Good Error',
    overview: 'Every error message must answer five questions: Where did this happen? What were we trying to do? What went wrong? Why did it fail? How can it be fixed? Without all five, the error is incomplete. Users get frustrated. Developers can\'t debug. Support tickets pile up.',
    sections: [
      {
        heading: 'The Five Questions',
        content: [
          '**Where did this happen?** Which component, which module, which system failed?',
          '',
          '**What were we trying to do?** What operation was in progress when the failure occurred?',
          '',
          '**What went wrong?** In user-friendly terms, what failed?',
          '',
          '**Why did it fail?** Technical details for debugging.',
          '',
          '**How can it be fixed?** Actionable suggestions for resolution.'
        ]
      },
      {
        heading: 'The Severity Hierarchy',
        content: [
          '**Critical:** The system cannot continue. Data may be corrupted. User must stop immediately.',
          '',
          '**Error:** This operation failed, but the system is stable. User should take action but can continue using other features.',
          '',
          '**Warning:** Something degraded but the operation continues. Using fallback values, skipping optional steps, etc.',
          '',
          '**Info:** Not actually an error. Informational status about what\'s happening.',
          '',
          'Choose the right level. Don\'t use Critical for recoverable errors. Don\'t use Warning for actual failures.'
        ]
      },
      {
        heading: 'Fail Fast, Fail Clear',
        content: [
          'When something is wrong, fail immediately with a clear message. Don\'t try to limp along. Don\'t partially complete operations. Don\'t leave the system in an inconsistent state.',
          '',
          'Detect the problem, report it clearly, and stop.',
          '',
          'Clean failure is better than partial success that creates subtle bugs later.'
        ]
      }
    ]
  }
];
